import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";

import { ExchangeRate } from "../types";

interface ExchangeRateFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ExchangeRate) => void;
  initialData?: ExchangeRate;
  loading?: boolean;
  mode: "create" | "edit";
}

const defaultExchangeRate: ExchangeRate = {
  currencyCode: "",
  exchangeRate: 0,
};

const ExchangeRateForm: React.FC<ExchangeRateFormProps> = ({
  open,
  onClose,
  initialData,
  loading = false,
  mode,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<ExchangeRate>(defaultExchangeRate);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData(defaultExchangeRate);
    }

    setErrors({});
  }, [initialData, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "exchangeRate") {
      const numValue = Math.max(0, parseFloat(value) || 0);
      setFormData({
        ...formData,
        [name]: numValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.currencyCode.trim()) {
      newErrors.currencyCode = "El código de moneda es requerido";
    } else if (formData.currencyCode.length !== 3) {
      newErrors.currencyCode = "El código de moneda debe tener 3 caracteres";
    }

    if (formData.exchangeRate <= 0) {
      newErrors.exchangeRate = "El tipo de cambio debe ser mayor que cero";
    } else if (!Number.isFinite(formData.exchangeRate)) {
      newErrors.exchangeRate = "El tipo de cambio debe ser un número válido";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {mode === "create" ? "Crear Nuevo Tipo de Cambio" : "Editar Tipo de Cambio"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <TextField
                name="currencyCode"
                label="Código de Moneda"
                value={formData.currencyCode}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.currencyCode}
                helperText={errors.currencyCode}
                disabled={mode === "edit"}
                inputProps={{ maxLength: 3 }}
                placeholder="USD"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="exchangeRate"
                label="Tipo de Cambio"
                type="number"
                value={formData.exchangeRate}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.exchangeRate}
                helperText={errors.exchangeRate}
                inputProps={{
                  step: "0.0001",
                  min: "0.0001",
                  pattern: "^[0-9]*[.,]?[0-9]+$",
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Guardar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ExchangeRateForm;
